import type { Provider, Visit } from '../readinessMock';
import {
  GRID_START_MIN,
  GRID_END_MIN,
  PX_PER_HOUR,
  blockTime,
  hourLabel,
} from '../readinessMock';

const GRID_HEIGHT = ((GRID_END_MIN - GRID_START_MIN) / 60) * PX_PER_HOUR;

function stateClass(v: Visit): string {
  if (v.state === 'not_ready') return ' warn';
  if (v.state === 'checking') return ' grey';
  return '';
}

/**
 * The day grid (rcm-22 §3): columns = providers / hygiene chairs with visits,
 * hours 8 AM–5 PM, a block per visit positioned by start + length, its left
 * edge coloured by readiness state. No production $, %, tiers or opportunity.
 */
export function DayGrid({
  columns,
  visits,
  isToday,
  nowMin,
  selectedId,
  filterNotReady,
  onSelect,
}: {
  columns: { provider: Provider; count: number }[];
  visits: Visit[];
  isToday: boolean;
  nowMin: number;
  selectedId: string | null;
  filterNotReady: boolean;
  onSelect: (id: string) => void;
}) {
  const hours: number[] = [];
  for (let m = GRID_START_MIN; m <= GRID_END_MIN - 60; m += 60) hours.push(m);
  const showNow = isToday && nowMin >= GRID_START_MIN && nowMin <= GRID_END_MIN;
  const nowTop = ((nowMin - GRID_START_MIN) / 60) * PX_PER_HOUR;

  return (
    <section className="rcal-card rcal-grid-card">
      <div
        className="rcal-cal"
        style={{ gridTemplateColumns: `52px repeat(${String(columns.length)}, minmax(0, 1fr))` }}
      >
        {/* header row */}
        <div />
        {columns.map((c) => (
          <div key={c.provider.id} className="rcal-ch">
            {c.provider.name}
            <span className="rcal-mono">
              {c.count} {c.count === 1 ? 'visit' : 'visits'}
            </span>
          </div>
        ))}

        {/* hour-label gutter */}
        <div className="rcal-hrs" style={{ height: GRID_HEIGHT }}>
          {hours.map((m) => (
            <b key={m} style={{ top: ((m - GRID_START_MIN) / 60) * PX_PER_HOUR }}>
              {hourLabel(m)}
            </b>
          ))}
        </div>

        {/* a positioned column per provider */}
        {columns.map((c, ci) => {
          const colVisits = visits.filter((v) => v.providerId === c.provider.id);
          return (
            <div
              key={c.provider.id}
              className={`rcal-col${filterNotReady ? ' focus-nr' : ''}`}
              style={{
                height: GRID_HEIGHT,
                background:
                  'repeating-linear-gradient(#fff 0, #fff 63px, #eef2f1 63px, #eef2f1 64px)',
              }}
            >
              {colVisits.map((v) => {
                const top = ((v.startMin - GRID_START_MIN) / 60) * PX_PER_HOUR + 2;
                const height = (v.lengthMin / 60) * PX_PER_HOUR - 4;
                const past = isToday && v.startMin < nowMin;
                const tall = v.lengthMin >= 90;
                return (
                  <button
                    key={v.id}
                    type="button"
                    className={`rcal-v${stateClass(v)}${past ? ' past' : ''}${
                      v.id === selectedId ? ' sel' : ''
                    }`}
                    style={{ top, height }}
                    onClick={() => {
                      onSelect(v.id);
                    }}
                  >
                    <span className="rcal-vt">{blockTime(v.startMin)}</span> <b>{v.patient}</b>
                    <span className="rcal-vr">{v.line}</span>
                    {tall ? (
                      <span className="rcal-vr" style={{ color: '#647174', fontWeight: 400 }}>
                        {v.procedure.label}
                        {v.procedure.cdt ? ` (${v.procedure.cdt})` : ''}
                      </span>
                    ) : null}
                  </button>
                );
              })}
              {showNow && ci === 0 ? (
                <div className="rcal-now" style={{ top: nowTop }}>
                  <i>{blockTime(nowMin)}</i>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      <div className="rcal-legend">
        <span>
          <i style={{ background: '#0e7c78' }} />
          Ready: checked the day before, covered as planned
        </span>
        <span>
          <i style={{ background: '#c98a1b' }} />
          Not ready: the reason is on the visit
        </span>
        <span>
          <i style={{ background: '#b8c2c0' }} />
          ELVA is still checking
        </span>
      </div>
    </section>
  );
}

/** Loading skeleton: 3 grey blocks per column (rcm-22 §5). */
export function DayGridSkeleton({ columnCount = 4 }: { columnCount?: number }) {
  const cols = Array.from({ length: columnCount });
  return (
    <section className="rcal-card rcal-grid-card">
      <div
        className="rcal-cal"
        style={{ gridTemplateColumns: `52px repeat(${String(columnCount)}, minmax(0, 1fr))` }}
      >
        <div />
        {cols.map((_, i) => (
          <div key={i} className="rcal-ch" style={{ color: 'transparent' }}>
            —<span className="rcal-mono">—</span>
          </div>
        ))}
        <div className="rcal-hrs" style={{ height: GRID_HEIGHT }} />
        {cols.map((_, ci) => (
          <div key={ci} className="rcal-col" style={{ height: GRID_HEIGHT }}>
            {[0, 1, 2].map((b) => (
              <div
                key={b}
                className="rcal-skel"
                style={{ top: 20 + b * 170, height: 80 }}
              />
            ))}
          </div>
        ))}
      </div>
    </section>
  );
}
